import { useState, useEffect, useCallback } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { useTranslation } from "react-i18next"
import { saveTranslations, loadTranslations } from "./translations"

const EventsManager = () => {
  const { i18n } = useTranslation("global")
  const [events, setEvents] = useState([])
  const [editingId, setEditingId] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [itTranslations, setItTranslations] = useState(null)
  const [enTranslations, setEnTranslations] = useState(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [itData, enData] = await Promise.all([
          loadTranslations("it"),
          loadTranslations("en"),
        ])

        if (
          !itData || !enData ||
          Array.isArray(itData) || Array.isArray(enData) ||
          typeof itData !== "object" || typeof enData !== "object" ||
          Object.keys(itData).length === 0 || Object.keys(enData).length === 0
        ) {
          throw new Error("Failed to load translations: received empty or invalid data. Try refreshing.")
        }

        setItTranslations(itData)
        setEnTranslations(enData)

        const itItems = itData?.events?.items || []
        const enItems = enData?.events?.items || []

        const merged = itItems.map((itEvt, index) => {
          const enEvt = enItems[index] || {}
          return {
            dragId: `event-${index}`,
            id: itEvt.id || `evt-${Date.now()}-${index}`,
            image: itEvt.image || "",
            date: itEvt.date || "",
            time: itEvt.time || "",
            titleIt: itEvt.title || "",
            titleEn: enEvt.title || "",
            descriptionIt: itEvt.description || "",
            descriptionEn: enEvt.description || "",
          }
        })

        setEvents(merged)
      } catch (error) {
        console.error("[EventsManager] Error loading:", error)
        setMessage({ type: "error", text: "Failed to load events: " + error.message })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleDragEnd = useCallback((result) => {
    if (!result.destination) return
    setEvents((prev) => {
      const items = Array.from(prev)
      const [reordered] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reordered)
      return items.map((item, index) => ({ ...item, dragId: `event-${index}` }))
    })
  }, [])

  const updateEvent = useCallback((dragId, field, value) => {
    setEvents((prev) =>
      prev.map((e) => (e.dragId === dragId ? { ...e, [field]: value } : e))
    )
  }, [])

  const addEvent = useCallback(() => {
    const newDragId = `event-${Date.now()}`
    const newEvent = {
      dragId: newDragId,
      id: `evt-${Date.now()}`,
      image: "",
      date: new Date().toISOString().slice(0, 10),
      time: "20:00",
      titleIt: "Nuovo evento",
      titleEn: "New event",
      descriptionIt: "Descrizione dell'evento...",
      descriptionEn: "Event description...",
    }
    setEvents((prev) => [...prev, newEvent])
    setEditingId(newDragId)
  }, [])

  const deleteEvent = useCallback((dragId) => {
    if (window.confirm("Delete this event?")) {
      setEvents((prev) => {
        const filtered = prev.filter((e) => e.dragId !== dragId)
        return filtered.map((item, index) => ({ ...item, dragId: `event-${index}` }))
      })
    }
  }, [])

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!itTranslations || !enTranslations) {
      setMessage({ type: "error", text: "Translations not loaded." })
      return
    }

    setIsSaving(true)
    setMessage({ type: "loading", text: "Saving events..." })

    try {
      const itItems = events.map(({ id, image, date, time, titleIt, descriptionIt }) => ({
        id,
        image,
        date,
        time,
        title: titleIt,
        description: descriptionIt,
      }))

      const enItems = events.map(({ id, image, date, time, titleEn, descriptionEn }) => ({
        id,
        image,
        date,
        time,
        title: titleEn,
        description: descriptionEn,
      }))

      const updatedIt = JSON.parse(JSON.stringify(itTranslations))
      const updatedEn = JSON.parse(JSON.stringify(enTranslations))

      if (!updatedIt.events) updatedIt.events = {}
      if (!updatedEn.events) updatedEn.events = {}

      updatedIt.events.items = itItems
      updatedEn.events.items = enItems

      await saveTranslations("it", updatedIt)
      await saveTranslations("en", updatedEn)

      i18n.addResourceBundle("it", "global", updatedIt, true, true)
      i18n.addResourceBundle("en", "global", updatedEn, true, true)

      setItTranslations(updatedIt)
      setEnTranslations(updatedEn)

      setMessage({ type: "success", text: "Events saved successfully! Reloading..." })
      setTimeout(() => window.location.reload(), 2000)
    } catch (error) {
      console.error("[EventsManager] Save error:", error)
      setMessage({ type: "error", text: "Failed to save: " + error.message })
      setIsSaving(false)
    }
  }

  const handleEventImageUpload = async (file, eventDragId, eventId) => {
    if (!file) return
    if (!file.type.match(/^image\/(jpeg|webp)$/)) {
      setMessage({ type: "error", text: "Only JPG/JPEG and WebP files are allowed" })
      return
    }

    const ext = file.type === "image/webp" ? "webp" : "jpeg"
    const formData = new FormData()
    formData.append("image", file)
    const filename = `event-${eventId}.${ext}`
    formData.append("filename", filename)

    try {
      setMessage({ type: "loading", text: "Uploading event image..." })

      let successCount = 0
      let lastError = null

      try {
        const response1 = await fetch("https://www.casadipinta.com/upload-image.php", {
          method: "POST",
          body: formData,
        })
        if (response1.ok) {
          const data = await response1.json()
          if (data.success) successCount++
        }
      } catch (error) {
        console.warn("Upload to www.casadipinta.com failed:", error)
        lastError = error
      }

      try {
        const response2 = await fetch("https://works.casadipinta.com/upload-image.php", {
          method: "POST",
          body: formData,
        })
        if (response2.ok) {
          const data = await response2.json()
          if (data.success) successCount++
        }
      } catch (error) {
        console.warn("Upload to works.casadipinta.com failed:", error)
        lastError = error
      }

      if (successCount > 0) {
        const imageUrl = `https://www.casadipinta.com/img/${filename}?v=${new Date().getTime()}`
        updateEvent(eventDragId, "image", imageUrl)
        const msg = successCount === 2
          ? "Image uploaded successfully to both servers"
          : `Image uploaded to ${successCount}/2 servers`
        setMessage({ type: "success", text: msg })
      } else {
        throw lastError || new Error("Upload failed on both servers")
      }
    } catch (error) {
      console.error("Upload error:", error)
      setMessage({ type: "error", text: error.message || "Failed to upload image" })
    }
  }

  const isPast = (dateStr) => {
    if (!dateStr) return false
    return dateStr < new Date().toISOString().slice(0, 10)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading events...</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto" style={{ maxHeight: "calc(100vh - 100px)" }}>
      <div className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading text-secondary-200">Events Manager</h1>
            <p className="text-gray-600 text-xs sm:text-sm">
              Manage upcoming events. Shared: image, date, time. Per-language: title, description.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium transition-colors ${
              isSaving
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-secondary-200 text-white hover:bg-secondary-400"
            }`}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {message && (
          <div
            className={`mb-4 p-3 rounded-lg ${
              message.type === "success"
                ? "bg-green-100 text-green-700"
                : message.type === "error"
                  ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
            }`}
          >
            {message.text}
          </div>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="events-list">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-3"
              >
                {events.map((event, index) => (
                  <Draggable key={event.dragId} draggableId={event.dragId} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`bg-white rounded-lg border-2 transition-all ${
                          snapshot.isDragging
                            ? "border-secondary-400 shadow-lg"
                            : "border-gray-200"
                        } ${editingId === event.dragId ? "ring-2 ring-secondary-400" : ""}`}
                      >
                        <div className="flex items-start gap-3 p-4">
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="mt-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                          >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                            </svg>
                          </div>

                          <div className="flex-1">
                            {editingId === event.dragId ? (
                              <div className="space-y-3 sm:space-y-4">
                                {/* Shared fields */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      Date (shared)
                                    </label>
                                    <input
                                      type="date"
                                      value={event.date}
                                      onChange={(e) => updateEvent(event.dragId, "date", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      Time (shared)
                                    </label>
                                    <input
                                      type="time"
                                      value={event.time}
                                      onChange={(e) => updateEvent(event.dragId, "time", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                                    />
                                  </div>
                                </div>

                                {/* Image upload + URL */}
                                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                  <label className="block text-xs font-medium text-gray-500 mb-2">
                                    Event Image (shared, optional)
                                  </label>
                                  <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="flex-1 space-y-2">
                                      <input
                                        type="file"
                                        accept="image/jpeg,image/webp"
                                        onChange={(e) => handleEventImageUpload(e.target.files[0], event.dragId, event.id)}
                                        className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-secondary-200 file:text-white hover:file:bg-secondary-400"
                                      />
                                      <div className="flex items-center gap-2">
                                        <input
                                          type="text"
                                          value={event.image}
                                          onChange={(e) => updateEvent(event.dragId, "image", e.target.value)}
                                          className="flex-1 px-3 py-1.5 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                                          placeholder="/img/event.webp or upload above"
                                        />
                                        {event.image && (
                                          <button
                                            type="button"
                                            onClick={() => updateEvent(event.dragId, "image", "")}
                                            className="text-red-400 hover:text-red-600 text-xs px-2 py-1.5 border border-red-200 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
                                          >
                                            Clear
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    {event.image && (
                                      <div className="shrink-0">
                                        <img
                                          src={event.image}
                                          alt="Event preview"
                                          className="w-20 h-20 object-cover rounded-lg border shadow-sm"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Per-language title */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">
                                          IT
                                        </span>
                                        Title
                                      </span>
                                    </label>
                                    <input
                                      type="text"
                                      value={event.titleIt}
                                      onChange={(e) => updateEvent(event.dragId, "titleIt", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">
                                          EN
                                        </span>
                                        Title
                                      </span>
                                    </label>
                                    <input
                                      type="text"
                                      value={event.titleEn}
                                      onChange={(e) => updateEvent(event.dragId, "titleEn", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                                    />
                                  </div>
                                </div>

                                {/* Per-language description */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold">
                                          IT
                                        </span>
                                        Description
                                      </span>
                                    </label>
                                    <textarea
                                      value={event.descriptionIt}
                                      onChange={(e) => updateEvent(event.dragId, "descriptionIt", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent resize-none"
                                      rows={3}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      <span className="inline-flex items-center gap-1">
                                        <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold">
                                          EN
                                        </span>
                                        Description
                                      </span>
                                    </label>
                                    <textarea
                                      value={event.descriptionEn}
                                      onChange={(e) => updateEvent(event.dragId, "descriptionEn", e.target.value)}
                                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent resize-none"
                                      rows={3}
                                    />
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => setEditingId(null)}
                                  className="px-4 py-2 bg-secondary-200 text-white rounded-lg hover:bg-secondary-400 transition-colors"
                                >
                                  Done Editing
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => setEditingId(event.dragId)}
                                className="cursor-pointer"
                              >
                                <div className="flex gap-3">
                                  {event.image && (
                                    <img
                                      src={event.image}
                                      alt=""
                                      className="w-12 h-12 object-cover rounded-lg border shrink-0"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                      <span
                                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                          isPast(event.date)
                                            ? "bg-red-100 text-red-700"
                                            : "bg-green-100 text-green-700"
                                        }`}
                                      >
                                        {isPast(event.date) ? "Past" : "Upcoming"}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {event.date} {event.time && `at ${event.time}`}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                      <div className="flex gap-2">
                                        <span className="w-5 h-4 rounded bg-green-600 text-white text-[10px] flex items-center justify-center font-bold shrink-0">
                                          IT
                                        </span>
                                        <div>
                                          <p className="font-semibold text-secondary-200">{event.titleIt}</p>
                                          <p className="text-gray-600 line-clamp-1">{event.descriptionIt}</p>
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <span className="w-5 h-4 rounded bg-blue-600 text-white text-[10px] flex items-center justify-center font-bold shrink-0">
                                          EN
                                        </span>
                                        <div>
                                          <p className="font-semibold text-secondary-200">{event.titleEn}</p>
                                          <p className="text-gray-600 line-clamp-1">{event.descriptionEn}</p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => deleteEvent(event.dragId)}
                            className="text-red-400 hover:text-red-600 transition-colors p-1"
                            title="Delete event"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <button
          type="button"
          onClick={addEvent}
          className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-secondary-400 hover:text-secondary-400 transition-colors"
        >
          + Add New Event
        </button>

        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-800 mb-2">How to use:</h3>
          <ol className="text-amber-700 text-sm space-y-1 list-decimal list-inside">
            <li>Click on an event to edit its details</li>
            <li>Upload a JPEG image or enter a URL manually — images are shared across both languages</li>
            <li>Date and time are also shared across both languages</li>
            <li>Title and description are separate for IT and EN</li>
            <li>Past events are hidden from the public Events page automatically</li>
            <li>Drag events to reorder them</li>
            <li>Click "Save Changes" to update both languages on the server</li>
          </ol>
        </div>
      </div>
    </div>
  )
}

export default EventsManager
